import axios, { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import jwtDecode from 'jwt-decode';
import moment from 'moment';
import { notification } from '_atoms';
import { ROUTES } from 'router/constants';
import history from './history';

const controllersMap = new Map<string | undefined, AbortController>();

interface JwtPayload {
  exp: number;
  [key: string]: unknown;
}

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_HOST}/api/`,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: (params: Record<string, unknown>) =>
    Object.entries(params)
      .flatMap(([key, value]) =>
        Array.isArray(value)
          ? `${key}=${value.join(',')}`
          : value
          ? `${key}=${encodeURIComponent(value as string)}`
          : ''
      )
      .filter(v => v)
      .join('&'),
});

export const checkToken = (): boolean => {
  const data = jwtDecode<JwtPayload>(window.localStorage.getItem('access_token') as string);
  return data.exp > moment().unix();
};

export interface FetchWrapperParams {
  url: string;
  method?: Method;
  /* Accepts any object; named shapes don't satisfy `Record<string, unknown>`
     without an explicit index signature, so this stays loose for callers. */
  body?: object;
  headers?: Record<string, string>;
  version?: string;
  skipTokenCheck?: boolean;
  key?: string;
  signal?: AbortSignal;
  showErrorNotification?: boolean;
}

export async function fetchWrapper<T = unknown>({
  url,
  method = 'GET',
  body = {},
  headers = {},
  version = 'v1',
  skipTokenCheck,
  key,
  signal,
  showErrorNotification = true,
}: FetchWrapperParams): Promise<T | null | undefined> {
  if (key && controllersMap.has(key)) {
    controllersMap.get(key)!.abort();
  }

  const controller = signal ? undefined : new AbortController();
  if (controller) controllersMap.set(key, controller);

  const clearController = () => {
    if (controller && controllersMap.get(key) === controller) {
      controllersMap.delete(key);
    }
  };

  try {
    const token = window.localStorage.getItem('access_token');
    if (token && checkToken()) {
      headers.Authorization = `JWT ${token}`;
    } else if (!skipTokenCheck) {
      history.push(ROUTES.LOGIN);
      return;
    }

    const config: AxiosRequestConfig = {
      method,
      url: `${version}/${url}`,
      headers,
      signal: signal ?? controller?.signal,
    };

    if (method === 'GET') {
      config.params = Object.fromEntries(
        Object.entries(body).filter(([, value]) => value !== undefined && value !== '')
      );
    } else {
      config.data = body;
    }

    const response: AxiosResponse<T> = await api(config);

    clearController();

    if (response.status === 204) return null;

    const contentType = response.headers['content-type'];
    if (typeof contentType === 'string' && contentType.includes('application/json')) {
      return response.data;
    }
    return response.data;
  } catch (error) {
    const { response } = error as {
      response?: { status: number; statusText?: string; data?: Record<string, string | string[]> };
    };

    clearController();

    if (response) {
      const { status, data } = response;

      if (status === 401) {
        window.localStorage.removeItem('access_token');
        history.push(ROUTES.LOGIN);
        return Promise.reject(error);
      }

      if (status === 403) {
        if (showErrorNotification) {
          notification({ message: 'You do not have permission to perform this action.' });
        }
        return Promise.reject(error);
      }

      if (status === 400) {
        if (data && showErrorNotification) {
          let errorMessage = '';
          Object.keys(data).forEach(k => {
            const item = data[k];
            errorMessage += typeof item === 'string' ? item : item.join(', ');
          });
          notification({ message: errorMessage });
        } else if (showErrorNotification) {
          notification({ message: 'Something went wrong. Please check your data.' });
        }
        return Promise.reject(error);
      }

      if (status === 500) {
        if (showErrorNotification) {
          notification({ message: response.statusText || '500 error. Please contact your Administrator' });
        }
        return Promise.reject(error);
      }

      try {
        return Promise.reject(data);
      } catch (e) {
        return Promise.reject(error);
      }
    }

    throw error;
  }
}

export async function asyncForEach<T>(
  array: T[],
  callback: (item: T, index: number, arr: T[]) => Promise<void> | void
): Promise<void> {
  for (let index = 0; index < array.length; index += 1) {
    // eslint-disable-next-line no-await-in-loop
    await callback(array[index], index, array);
  }
}

export function dataURItoBlob(dataURI: string): Blob {
  let byteString: string;
  if (dataURI.split(',')[0].indexOf('base64') >= 0) byteString = atob(dataURI.split(',')[1]);
  else byteString = unescape(dataURI.split(',')[1]);
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ia], { type: mimeString });
}

export const snakeCase = (string: string): string =>
  string
    .replace(/\W+/g, ' ')
    .split(/ |\B(?=[A-Z])/)
    .map(word => word.toLowerCase())
    .join('_');
